import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Stethoscope, 
  Plus, 
  Trash2, 
  Calendar,
  User
} from 'lucide-react';
import { createDoctorNote, getDoctorNotes, deleteDoctorNote } from '@/services/api';
import { toast } from 'sonner';

export default function DoctorNotesPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form states
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [doctorName, setDoctorName] = useState('');

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      const data = await getDoctorNotes();
      setNotes(data);
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Failed to load doctor notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Please enter title and content');
      return;
    }

    setCreating(true);
    try {
      await createDoctorNote(title, content, doctorName);
      toast.success('Note created successfully!');
      setDialogOpen(false);
      resetForm();
      await loadNotes();
    } catch (error) {
      console.error('Create note error:', error);
      toast.error('Failed to create note');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteDoctorNote(noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      console.error('Delete note error:', error);
      toast.error('Failed to delete note');
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setDoctorName('');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="doctor-notes-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Doctor Notes
            </h1>
            <p className="text-slate-600 mt-1">Keep track of important notes from your consultations</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6 btn-active" data-testid="add-note-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Doctor Note</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Follow-up consultation"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="note-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="doctorName">Doctor Name</Label>
                  <Input
                    id="doctorName"
                    placeholder="e.g., Dr. Smith"
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    data-testid="note-doctor-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Notes *</Label>
                  <Textarea
                    id="content"
                    placeholder="Enter consultation notes, recommendations, prescriptions..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={6}
                    data-testid="note-content-input"
                  />
                </div>

                <Button
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="create-note-btn"
                >
                  {creating ? 'Creating...' : 'Save Note'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton h-48 rounded-xl" />
            ))}
          </div>
        ) : notes.length === 0 ? (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="py-12 text-center">
              <Stethoscope className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No doctor notes yet</p>
              <p className="text-sm text-slate-400 mt-1">Add notes from your consultations</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {notes.map((note, index) => (
              <Card 
                key={note.id} 
                className="bg-white border-slate-200 shadow-sm card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {note.title}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(note.id)}
                      className="text-slate-400 hover:text-red-600 -mt-2 -mr-2"
                      data-testid={`delete-note-${note.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    {note.doctor_name && (
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {note.doctor_name}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(note.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
                    {note.content}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
